/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Empty } from "../tournamentPage/MatchesPanel";
import { IcSearch, IcTrophy } from "../tournamentPage/icons";
import { norm, type BatLine, type BowlLine } from "../teamsTab/teamsData";
import { Avatar } from "./SquadTab";
import type { TeamCtx } from "./TeamPage";

type Line = BatLine | BowlLine;
type Cat = {
  k: string; label: string; kind: "bat" | "bow"; value: (a: any) => number; fmt?: (v: number) => string;
  sub: (a: any) => string; ok: (a: any) => boolean; asc?: boolean; min?: string; none: string;
};

const dismissals = (a: BatLine) => a.inn - a.no;
const bowlAve = (a: BowlLine) => (a.wkts ? a.runs / a.wkts : 0);
const f1 = (v: number) => v.toFixed(1);

const CATS: Cat[] = [
  { k: "runs", label: "Most runs", kind: "bat", value: (a: BatLine) => a.runs, sub: (a: BatLine) => `Inn ${a.inn} · Ave ${a.ave ? f1(a.ave) : "–"}`, ok: (a: BatLine) => a.inn > 0, none: "No one has batted yet." },
  { k: "wkts", label: "Most wickets", kind: "bow", value: (a: BowlLine) => a.wkts, sub: (a: BowlLine) => `${a.overs} ov · Econ ${a.econ ? a.econ.toFixed(2) : "–"}`, ok: (a: BowlLine) => a.inn > 0, none: "No wickets taken yet." },
  { k: "ave", label: "Best average", kind: "bat", value: (a: BatLine) => a.ave, fmt: f1, sub: (a: BatLine) => `${a.runs} runs · ${a.inn} inn`, ok: (a: BatLine) => a.inn >= 2 && dismissals(a) > 0, min: "min 2 innings", none: "No one has two innings with a dismissal yet." },
  { k: "sr", label: "Best strike rate", kind: "bat", value: (a: BatLine) => a.sr, fmt: f1, sub: (a: BatLine) => `${a.runs} runs · ${a.inn} inn`, ok: (a: BatLine) => a.inn >= 2 && a.runs > 0, min: "min 2 innings", none: "No one has two innings yet." },
  { k: "econ", label: "Best economy", kind: "bow", value: (a: BowlLine) => a.econ, fmt: (v) => v.toFixed(2), sub: (a: BowlLine) => `${a.wkts} wkts · ${a.overs} ov`, ok: (a: BowlLine) => a.overs >= 2, asc: true, min: "min 2 overs", none: "No one has bowled 2 overs yet." },
  { k: "s6", label: "Most 6s", kind: "bat", value: (a: BatLine) => a.s6, sub: (a: BatLine) => `${a.f4} fours`, ok: (a: BatLine) => a.s6 > 0, none: "No sixes yet." },
  { k: "hs", label: "Highest scores", kind: "bat", value: (a: BatLine) => a.hs, sub: (a: BatLine) => `${a.runs} runs · ${a.inn} inn`, ok: (a: BatLine) => a.hs > 0, none: "No scores yet." },
  { k: "f4", label: "Most 4s", kind: "bat", value: (a: BatLine) => a.f4, sub: (a: BatLine) => `${a.s6} sixes`, ok: (a: BatLine) => a.f4 > 0, none: "No fours yet." },
  { k: "c50", label: "Most 50s", kind: "bat", value: (a: BatLine) => a.c50, sub: (a: BatLine) => `${a.runs} runs`, ok: (a: BatLine) => a.c50 > 0, none: "No fifties yet." },
  { k: "c100", label: "Most 100s", kind: "bat", value: (a: BatLine) => a.c100, sub: (a: BatLine) => `High score ${a.hs}`, ok: (a: BatLine) => a.c100 > 0, none: "No hundreds yet." },
  { k: "mdn", label: "Most maidens", kind: "bow", value: (a: BowlLine) => a.mdn, sub: (a: BowlLine) => `${a.overs} ov`, ok: (a: BowlLine) => a.mdn > 0, none: "No maidens yet." },
];
const OVERVIEW = ["runs", "wkts", "ave", "sr", "econ", "s6"];
const MORE = ["hs", "f4", "c50", "c100", "mdn"];
const cat = (k: string) => CATS.find((c) => c.k === k) as Cat;

const rank = (c: Cat, lines: Line[]) => {
  const list = lines.filter(c.ok).sort((a, b) => (c.asc ? c.value(a) - c.value(b) : c.value(b) - c.value(a)) || a.name.localeCompare(b.name));
  let prev: number | null = null, rk = 0;
  return list.map((a, i) => { const v = c.value(a); if (v !== prev) { rk = i + 1; prev = v; } return { a, rk, v }; });
};

const BATC: [string, string, (a: BatLine) => number | string][] = [["mat", "Mat", (a) => a.mat], ["inn", "Inn", (a) => a.inn], ["no", "NO", (a) => a.no], ["runs", "Runs", (a) => a.runs], ["hs", "HS", (a) => a.hs], ["ave", "Ave", (a) => a.ave], ["sr", "SR", (a) => a.sr], ["c100", "100", (a) => a.c100], ["c50", "50", (a) => a.c50], ["f4", "4s", (a) => a.f4], ["s6", "6s", (a) => a.s6]];
const BOWC: [string, string, (a: BowlLine) => number | string][] = [["mat", "Mat", (a) => a.mat], ["inn", "Inn", (a) => a.inn], ["overs", "Overs", (a) => a.overs], ["mdn", "Mdns", (a) => a.mdn], ["runs", "Runs", (a) => a.runs], ["wkts", "Wkts", (a) => a.wkts], ["ave", "Ave", (a) => bowlAve(a)], ["econ", "Econ", (a) => a.econ], ["w5", "5w", (a) => a.w5]];
const show = (key: string, v: number | string, kind: "bat" | "bow") => typeof v === "string" ? v : (key === "ave" || key === "sr") && kind === "bat" ? (v ? f1(v) : "–") : key === "econ" ? (v ? v.toFixed(2) : "–") : key === "ave" ? (v ? f1(v) : "–") : String(v);

const TeamStatsTab = ({ ctx }: { ctx: TeamCtx }) => {
  const navigate = useNavigate();
  const [view, setView] = useState<string | null>(null);
  const [sort, setSort] = useState<{ k: string; dir: 1 | -1 } | null>(null);
  const goPlayer = (id: string) => navigate(`/tournaments/player-profile?playerId=${encodeURIComponent(id)}`);

  const past = useMemo(() => ctx.matches.filter((m) => m.bucket === "past"), [ctx.matches]);
  const records = useMemo(() => {
    const mineName = norm(ctx.name);
    const inn: { runs: number; wkts: number; over: string }[] = [];
    let first = 0, firstWon = 0, chase = 0, chaseWon = 0;
    past.forEach((m) => {
      const list: any[] = m.raw?.inningScorecardList ?? [];
      list.filter((i) => norm(i.battingTeamName) === mineName).forEach((i) => inn.push({ runs: i.runs ?? 0, wkts: i.wicket ?? 0, over: `${i.over ?? 0}.${i.ball ?? 0}` }));
      if (m.outcome === "NR" || m.outcome === "T") return;
      const side = norm(m.a.name) === mineName ? m.a : m.b;
      const batFirst = norm(list[0]?.battingTeamName) === mineName;
      const chased = list.length > 1 && norm(list[1]?.battingTeamName) === mineName;
      if (batFirst) { first++; if (side.won) firstWon++; }
      else if (chased) { chase++; if (side.won) chaseWon++; }
    });
    const hi = inn.reduce<typeof inn[number] | null>((b, x) => (!b || x.runs > b.runs ? x : b), null);
    const lo = inn.length > 1 ? inn.reduce<typeof inn[number] | null>((b, x) => (!b || x.runs < b.runs ? x : b), null) : null;
    return { hi, lo, first, firstWon, chase, chaseWon, f4: ctx.batting.reduce((t, a) => t + a.f4, 0), s6: ctx.batting.reduce((t, a) => t + a.s6, 0), innCount: inn.length };
  }, [past, ctx.name, ctx.batting]);

  if (ctx.matchesLoading || ctx.statsLoading) return <div className="empty"><p>Loading stats…</p></div>;
  if (past.length === 0 && ctx.batting.length === 0)
    return <Empty icon={<IcTrophy />} title="Stats appear after the first match" text="Batting and bowling leaderboards fill in automatically from the scorecards." />;

  const lines = (c: Cat): Line[] => (c.kind === "bat" ? ctx.batting : ctx.bowling);

  // ---------- full list ----------
  if (view) {
    const c = cat(view);
    const ranked = rank(c, lines(c));
    const cols = (c.kind === "bat" ? BATC : BOWC) as [string, string, (a: any) => number | string][];
    const activeKey = sort?.k ?? c.k; // category keys match the column keys
    const sortVal = (a: any, k: string) => { const col = cols.find((x) => x[0] === k); const v = col ? col[2](a) : 0; return typeof v === "number" ? v : 0; };
    const rows = sort ? [...ranked].sort((x, y) => sort.dir * (sortVal(y.a, sort.k) - sortVal(x.a, sort.k)) || x.a.name.localeCompare(y.a.name)) : ranked;
    // economy is the one column where lower is better, so it sorts ascending first
    const onSort = (k: string) => setSort((cur) => (cur && cur.k === k ? { k, dir: (-cur.dir) as 1 | -1 } : { k, dir: k === "econ" ? -1 : 1 }));
    return (
      <div className="tg-full">
        <div className="tg-fhead">
          <button className="btn" onClick={() => { setView(null); setSort(null); }}>← Back to stats overview</button>
          <div><h3>{c.label}</h3><span>{ctx.name} · this tournament{c.min ? ` · ${c.min}` : ""}</span></div>
        </div>
        <div className="chips">{[...OVERVIEW, ...MORE].map((k) => <button key={k} className={`chip${k === view ? " on" : ""}`} onClick={() => { setView(k); setSort(null); }}>{cat(k).label}</button>)}</div>
        {rows.length === 0 ? <Empty icon={<IcSearch />} title="Nothing here yet" text={c.none} /> : (
          <div className="tbl tg-table"><div className="tw"><table>
            <thead><tr><th className="l">#</th><th className="l">Player</th>{cols.map(([k, l]) => <th key={k} className={activeKey === k ? "act" : ""}><button onClick={() => onSort(k)} aria-label={`Sort by ${l}`}>{l}{sort?.k === k ? (sort.dir === 1 ? " ▼" : " ▲") : ""}</button></th>)}</tr></thead>
            <tbody>{rows.map(({ a, rk }) => (
              <tr key={a.id} className="click" onClick={() => goPlayer(a.id)} tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") goPlayer(a.id); }}>
                <td className="l rk">{sort ? "" : rk}</td>
                <td className="l"><span className="pn"><Avatar p={a} color={ctx.color} size={30} /><b>{a.name}</b></span></td>
                {cols.map(([k, , get]) => <td key={k} className={activeKey === k ? "act" : ""}>{show(k, get(a), c.kind)}</td>)}
              </tr>))}</tbody>
          </table></div></div>
        )}
        <p className="tg-foot">{rows.length} {rows.length === 1 ? "player" : "players"} ranked{c.min ? ` · ${c.min}` : ""} · Click a column to sort</p>
      </div>
    );
  }

  // ---------- overview ----------
  const tile = (k: string, v: React.ReactNode, s?: string) => <div className="rtile"><small>{k}</small><b>{v}</b>{s && <span>{s}</span>}</div>;
  const fmtInn = (x: { runs: number; wkts: number; over: string } | null) => (x ? `${x.runs}${x.wkts >= 10 ? "" : `/${x.wkts}`}` : "–");
  return (
    <>
      <section className="box">
        <div className="box-h"><h3>Team records</h3><span className="sub">{past.length} {past.length === 1 ? "match" : "matches"} played</span></div>
        <div className="rtiles">
          {tile("Highest total", fmtInn(records.hi), records.hi ? `${records.hi.over} ov` : "No innings yet")}
          {tile("Lowest total", fmtInn(records.lo), records.lo ? `${records.lo.over} ov` : "Needs 2 innings")}
          {tile("Won batting first", records.first ? `${records.firstWon} of ${records.first}` : "–", records.first ? undefined : "Hasn't batted first yet")}
          {tile("Won chasing", records.chase ? `${records.chaseWon} of ${records.chase}` : "–", records.chase ? undefined : "Hasn't chased yet")}
          {tile("Fours · sixes", `${records.f4} · ${records.s6}`)}
        </div>
      </section>
      <div className="days"><div className="summary" style={{ marginLeft: 0 }}>Players of <b>{ctx.name}</b> in this tournament</div></div>
      <div className="lgrid">
        {OVERVIEW.map((k) => {
          const c = cat(k);
          const top = rank(c, lines(c)).slice(0, 3);
          return (
            <section className="box lcard" key={k}>
              <div className="box-h"><h3>{c.label}</h3>{c.min && <span className="sub">{c.min}</span>}<button className="link" onClick={() => setView(k)}>View full list ›</button></div>
              {top.length === 0 ? <p className="lnone">{c.none}</p> : top.map(({ a, rk, v }) => (
                <button key={a.id} className={`lrow${rk === 1 ? " first" : ""}`} onClick={() => goPlayer(a.id)}>
                  <i>{rk}</i><Avatar p={a} color={ctx.color} size={34} /><span><b>{a.name}</b><small>{c.sub(a)}</small></span><em className="n">{c.fmt ? c.fmt(v) : v}</em>
                </button>
              ))}
            </section>
          );
        })}
      </div>
      <div className="days"><div className="summary" style={{ marginLeft: 0 }}>More lists</div></div>
      <div className="chips">{MORE.map((k) => <button key={k} className="chip" onClick={() => setView(k)}>{cat(k).label}</button>)}</div>
    </>
  );
};

export default TeamStatsTab;
